import { useEffect, useState } from 'react'
import { Maybe, User, UserRole } from '../../common/types'
import { initialValues, QueryValues } from './TowingJobFilter'

interface TowingJobFilterProps {
  filters: {
    visibility?: string[]
    labels?: string[]
    stations?: number[]
  }
}

export const TOWING_JOB_FILTERS_KEY = 'towing_job_filters'

export const useTowingJobFilters = ({
  currentUser,
  options,
  ready,
  onSetValues,
  onResetValues,
}: {
  currentUser: Maybe<User>
  options?: TowingJobFilterProps
  ready: boolean
  onSetValues?: (values: QueryValues) => void
  onResetValues?: (values: QueryValues) => void
}) => {
  const [values, setValues] = useState<QueryValues | null>(null)
  const [defaultFilters, setDefaultFilters] = useState<QueryValues | null>(null)

  useEffect(() => {
    if (!ready || values !== null) return
    const { filters, status, userId, defaultFilters: defaults } = getInitialFilters(currentUser, options)
    setDefaultFilters(defaults)
    setValues({ userId, filters, status, search: '' })
  }, [ready]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSetValues = (next: QueryValues) => {
    const valuesToSave = { ...next, userId: currentUser?.user_id ?? null }
    setValues(valuesToSave)
    if (onSetValues) onSetValues(valuesToSave)
  }

  const resetToDefaultOptions = () => {
    if (!defaultFilters) return
    setValues(defaultFilters)
    if (onResetValues) onResetValues(defaultFilters)
  }

  if (values === null || defaultFilters === null) return undefined

  return {
    isDefaultModified: JSON.stringify(values) !== JSON.stringify(defaultFilters),
    values,
    setValues: handleSetValues,
    resetToDefaultOptions,
  }
}

function getInitialFilters(currentUser: Maybe<User>, options?: TowingJobFilterProps) {
  let localStorageFilters = getLocalStorage(TOWING_JOB_FILTERS_KEY)
  const defaultFilters = getDefaultFilters(currentUser, options)

  if (localStorageFilters) {
    const resetOperators = !currentUser || ![UserRole.main_user, UserRole.duty_officer].includes(currentUser.role)
    if (resetOperators) {
      localStorageFilters.filters.operators = []
    }

    return { ...localStorageFilters, defaultFilters: defaultFilters }
  }

  return { ...defaultFilters, defaultFilters: defaultFilters }
}

const getLocalStorage = (key: string): Maybe<QueryValues> => {
  const storedValue = localStorage.getItem(key)

  const result = storedValue ? JSON.parse(storedValue) : null

  if (!result) return null

  return {
    filters: result.filters || initialValues.filters,
    status: result.status || initialValues.status,
    search: typeof result.search === 'string' ? result.search : '',
    userId: result.userId ? Number(result.userId) : null,
  }
}

function getDefaultFilters(currentUser: Maybe<User>, options?: TowingJobFilterProps) {
  const overrideOptions = options ? options.filters : {}
  const userId = currentUser?.user_id ?? null
  const defaultOptions = { ...initialValues, userId, filters: { ...initialValues.filters, ...overrideOptions } }
  return defaultOptions
}
